<template>
  <!-- Mode « nu » : le contenu seul, sans carte ni en-tête. Sert aux blocs
       regroupés (ex. « Le client » = informations personnelles + particulier +
       professionnel dans une seule section de la fiche demande). -->
  <div v-if="bare" class="section-bare">
    <div v-if="useGrid" class="detail-grid">
      <slot></slot>
    </div>
    <slot v-else></slot>
    <slot name="footer" :collapsed="false"></slot>
  </div>

  <div v-else ref="rootRef" class="collapsible-section" :data-testid="`collapsible-section-${storageKey || title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`">
    <Card class="detail-section">
      <div
        class="section-header"
        role="button"
        tabindex="0"
        :aria-expanded="!collapsed"
        :data-testid="`collapsible-section-header-${storageKey || title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`"
        @click="toggle"
        @keydown.enter.prevent="toggle"
        @keydown.space.prevent="toggle"
      >
        <div class="section-header-left">
          <span v-if="icon" class="section-icon">
            <i :class="icon"></i>
          </span>
          <component :is="`h${headingLevel}`">{{ title }}</component>
          <slot name="header-extra"></slot>
        </div>
        <div class="section-header-right">
          <!-- Les pastilles d'état (« Visible uniquement par l'équipe »,
               « N non lus ») se rangent ici et non contre le titre : quelle que
               soit la longueur du titre, on les cherche toujours au même
               endroit — même règle que le compteur. -->
          <slot name="header-right"></slot>
          <Badge v-if="count !== null" variant="info" size="xs" class="section-count">{{ count }}</Badge>
          <i
            class="fas fa-chevron-down section-chevron"
            :class="{ 'is-expanded': !collapsed }"
            aria-hidden="true"
          ></i>
        </div>
      </div>
      <div
        class="section-content"
        :class="{ 'collapsed': collapsed }"
      >
        <div class="section-content-inner">
          <div class="detail-grid" v-if="useGrid">
            <slot></slot>
          </div>
          <slot v-else></slot>
        </div>
      </div>
      <slot name="footer" :collapsed="collapsed"></slot>
    </Card>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, inject } from 'vue'
import Badge from '../ui/Badge.vue'
import Card from '../ui/Card.vue'
import { useSectionTreeRegistry } from '../../composables/useSectionTree.js'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  /**
   * NIVEAU DU TITRE, pour que la section se range là où elle est réellement
   * montée. Le `h2` par défaut vaut pour une fiche, où la section est de
   * premier niveau sous le titre de page ; une section posée à l'intérieur d'un
   * bloc déjà titré doit descendre d'autant, sans quoi la navigation par titres
   * d'un lecteur d'écran remonte d'un cran au milieu du contenu. Seule la
   * BALISE change : le style reste celui de `.section-header h2`.
   */
  headingLevel: {
    type: [Number, String],
    default: 2,
    validator: (valeur) => [2, 3, 4, 5, 6].includes(Number(valeur))
  },
  icon: {
    type: String,
    default: null
  },
  /** Pastille de compteur affichée à droite du titre (ex. nombre d'appels). */
  count: {
    type: [Number, String],
    default: null
  },
  storageKey: {
    type: String,
    default: null
  },
  defaultCollapsed: {
    type: Boolean,
    default: false
  },
  useGrid: {
    type: Boolean,
    default: true
  },
  /** Rend le contenu sans carte ni en-tête, pour l'imbriquer dans une autre section. */
  bare: {
    type: Boolean,
    default: false
  },
  context: {
    type: String,
    default: 'default' // 'quote', 'contact', etc.
  }
})

// Un consommateur qui charge ses données à l'ouverture (ex. les sociétés
// liées, cf. useLinkedCompanies) a besoin de savoir QUAND la section s'ouvre,
// sans dupliquer la mécanique de repli/dépli à chaque endroit qui en a
// besoin.
const emit = defineEmits(['toggle'])

// Config optionnelle fournie par un parent (provide) pour découpler le
// comportement d'un contexte à l'autre. Le tunnel « Analyse du besoin » l'utilise
// pour ouvrir toutes les sections à l'ouverture (forceExpanded) et ne rien
// persister (persist: false) : le repli/dépli ne partage alors rien avec la
// page « Demande de devis ».
const sectionConfig = inject('collapsibleSectionConfig', null)
const persistState = sectionConfig?.persist !== false
const forceExpanded = sectionConfig?.forceExpanded === true

const collapsed = ref(forceExpanded ? false : props.defaultCollapsed)
const rootRef = ref(null)

// Sommaire de navigation (optionnel) : la section s'y inscrit si la page en
// fournit un, sinon elle reste totalement autonome. Le registre attribue l'id.
// Une section « nue » n'a pas d'en-tête propre : elle ne s'inscrit pas.
const sectionTree = props.bare ? null : useSectionTreeRegistry()
let unregisterFromTree = null

// Générer une clé de stockage unique
const getStorageKey = () => {
  if (props.storageKey) {
    return `collapsible-section-${props.context}-${props.storageKey}`
  }
  // Utiliser le titre comme clé si aucune clé n'est fournie
  const sanitizedTitle = props.title.toLowerCase().replace(/[^a-z0-9]/g, '-')
  return `collapsible-section-${props.context}-${sanitizedTitle}`
}

// Charger l'état depuis localStorage
const loadState = () => {
  if (!persistState) return
  try {
    const key = getStorageKey()
    const saved = localStorage.getItem(key)
    if (saved !== null) {
      collapsed.value = JSON.parse(saved)
    }
  } catch (err) {
    console.warn('Erreur lors du chargement de l\'état de la section:', err)
  }
}

// Sauvegarder l'état dans localStorage
const saveState = () => {
  if (!persistState) return
  try {
    const key = getStorageKey()
    localStorage.setItem(key, JSON.stringify(collapsed.value))
  } catch (err) {
    console.warn('Erreur lors de la sauvegarde de l\'état de la section:', err)
  }
}

const toggle = () => {
  collapsed.value = !collapsed.value
  saveState()
  emit('toggle', collapsed.value)
}

// Contrôles pilotés depuis le sommaire (idempotents).
const expand = () => {
  if (!collapsed.value) return
  collapsed.value = false
  saveState()
  emit('toggle', collapsed.value)
}
const collapse = () => {
  if (collapsed.value) return
  collapsed.value = true
  saveState()
  emit('toggle', collapsed.value)
}

// Le repli/dépli est entièrement porté par le CSS (piste de grille 0fr/1fr) :
// aucune mesure de hauteur ici.
onMounted(() => {
  if (props.bare) return
  loadState()

  // L'état mémorisé (localStorage) peut rouvrir la section dès le montage :
  // un consommateur qui n'écoute que les clics suivants raterait ce cas-là,
  // donc l'état de départ est aussi signalé ici, une fois `loadState()` passé.
  emit('toggle', collapsed.value)

  if (sectionTree) {
    unregisterFromTree = sectionTree.register({
      title: props.title,
      icon: props.icon,
      el: rootRef.value,
      collapsed,
      expand,
      collapse,
      toggle,
    })
  }
})

onBeforeUnmount(() => {
  unregisterFromTree?.()
})

// Piloter le dépli depuis la page qui contient la section : une action de la
// ligne de tête (« Lui écrire ») doit pouvoir ouvrir la section visée et y
// amener l'œil, sans passer par le registre de colonne.
defineExpose({
  collapsed,
  expand,
  collapse,
  toggle,
  scrollIntoView: () => rootRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
})
</script>

<style scoped>
.collapsible-section {
  scroll-margin-top: var(--spacing-6);
}

/* La carte porte l'en-tête à ras bord : le rembourrage vit dans l'en-tête et
   dans le contenu, pour que la zone cliquable couvre toute la largeur. */
.detail-section {
  padding: 0;
  margin-bottom: var(--spacing-4);
  overflow: hidden;
}

/* L'EN-TÊTE PASSE À LA LIGNE PLUTÔT QUE DE HACHER SON TITRE.
   Sans `flex-wrap`, le bloc de droite — pastille « Visible uniquement par
   l'équipe », compteur, chevron : près de trois cents pixels incompressibles —
   gardait sa largeur quoi qu'il arrive, et c'est le titre qui rétrécissait. En
   colonne étroite, « L'historique de collaboration » se répartissait alors sur
   trois lignes AUTOUR de la pastille, centrée verticalement : elle se lisait
   au milieu du titre, comme si elle en faisait partie (« L'historique de
   VISIBLE UNIQUEMENT PAR L'ÉQUIPE collaboration »). Le titre garde maintenant
   une largeur de lecture, et c'est la ligne de pastilles qui descend. */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-3);
  flex-wrap: wrap;
  cursor: pointer;
  user-select: none;
  padding: var(--spacing-4) var(--spacing-5);
  transition: background var(--transition-fast);
}

.section-header:hover {
  background: var(--color-bg-secondary);
}

.section-header:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* 220 px : la largeur en dessous de laquelle un titre de section commence à se
   couper n'importe où. C'est ce seuil qui décide du passage à la ligne. */
.section-header-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  flex: 1 1 220px;
  min-width: 0;
}

/* padding: 0 obligatoire — le h2 global (layout.css) porte le souligné des
   titres de page avec un padding-bottom : hérité ici, il gonfle la boîte du
   titre par le bas et le texte remonte au-dessus du centre de la ligne. */
.section-header :is(h2, h3, h4, h5, h6) {
  color: var(--color-primary-dark);
  margin: 0;
  padding: 0;
  /* Sans min-width, un titre long refuse de rétrécir dans la ligne flex et
     passe sous la pastille de compteur au lieu de revenir à la ligne. */
  min-width: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  border: none;
  line-height: 1.4;
}

/* Pastille d'icône : le repère visuel de la section, à l'accent du produit. */
.section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-lg);
  background: var(--color-primary-lightest);
  color: var(--color-primary);
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

/* Les glyphes Font Awesome portent leur propre interlignage : sans ce
   line-height, l'icône flotte de un ou deux pixels au-dessus du titre. */
.section-icon i {
  line-height: 1;
  display: block;
}

/* Compteur et chevron vivent ensemble à droite : le chiffre se lit d'un
   coup d'œil au même endroit sur toutes les cartes, quelle que soit la
   longueur du titre. */
/* `margin-left: auto` et non `justify-content` : une fois seul sur sa ligne, ce
   bloc serait ramené à gauche par le `space-between` du parent, et le chevron
   quitterait le coin où on va le chercher. */
.section-header-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  margin-left: auto;
  flex-shrink: 0;
}

.section-count {
  flex-shrink: 0;
}

/* Le chevron dit l'état ; c'est toute la ligne d'en-tête qui est cliquable. */
.section-chevron {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  flex-shrink: 0;
  transition: transform var(--transition-base), color var(--transition-fast);
}

.section-chevron.is-expanded {
  transform: rotate(180deg);
}

.section-header:hover .section-chevron {
  color: var(--color-text-secondary);
}

/* Repli/dépli : on anime la piste de grille (1fr ↔ 0fr), qui suit la hauteur
   réelle du contenu sans la mesurer. Un max-height arbitraire, lui, claque à
   la fermeture (valeur non interpolable) et se coupe à l'ouverture. */
.section-content {
  display: grid;
  grid-template-rows: 1fr;
  opacity: 1;
  transition: grid-template-rows var(--transition-slow), opacity var(--transition-slow);
}

.section-content.collapsed {
  grid-template-rows: 0fr;
  opacity: 0;
}

/* Le rembourrage vit ici (et pas sur la piste) pour s'animer vers 0 avec
   elle : sinon la carte repliée garderait une bande vide de sa hauteur. */
.section-content-inner {
  min-height: 0;
  overflow: hidden;
  padding: 0 var(--spacing-5) var(--spacing-5);
  transition: padding-bottom var(--transition-slow);
}

.section-content.collapsed .section-content-inner {
  padding-bottom: 0;
}

/* Les champs se rangent en colonnes de 200 px : deux par ligne dans la colonne
   « dossier » d'une fiche détail, davantage sur une page pleine largeur. */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0 var(--spacing-5);
}

/* Styles pour les sections sans grid (pour DetailItem) */
.section-content-inner > :deep(.detail-item) {
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    padding: var(--spacing-4);
  }

  .section-content-inner {
    padding: 0 var(--spacing-4) var(--spacing-4);
  }
}
</style>
